import type { AuthTenant } from './auth';

/**
 * Inject per-tenant branding into CSS variables so any component using
 * `hsl(var(--primary))` / `--tenant-logo-url` automatically re-themes.
 *
 * Phase 1: primary color (hex) + logo URL. Phase 2 plans subdomain routing.
 */
export function applyTenantBranding(tenant: AuthTenant | null): void {
  const root = document.documentElement;

  if (!tenant) {
    root.style.removeProperty('--primary');
    root.style.removeProperty('--ring');
    root.style.removeProperty('--tenant-logo-url');
    return;
  }

  const color = tenant.branding_primary_color;
  if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
    const hsl = hexToHsl(color);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--ring', hsl);
    }
  } else {
    root.style.removeProperty('--primary');
    root.style.removeProperty('--ring');
  }

  if (tenant.branding_logo_url) {
    root.style.setProperty('--tenant-logo-url', `url("${tenant.branding_logo_url}")`);
  } else {
    root.style.removeProperty('--tenant-logo-url');
  }
}

function hexToHsl(hex: string): string | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m || !m[1]) return null;
  const num = parseInt(m[1], 16);
  const r = ((num >> 16) & 0xff) / 255;
  const g = ((num >> 8) & 0xff) / 255;
  const b = (num & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h *= 60;
  }
  return `${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`;
}
